/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import StringResources from 'nls/StringResources';
import _ from 'underscore';

const Event = require('lib/@waca/core-client/js/core-client/ui/core/Events');

/** `
 * Class handle palette objects in the content service. 
*/
class PaletteService extends Event {
	constructor() {
		super(arguments);
		// Local cache of palette definitions. 
		this._cachedPalettes = {};
		this._paletteRequestPromise = {};
		this._cachedMRUPalettes = null;
	}

	initialize({ appController }) {
		this._glassContext = appController.glassContext;
		this._logger = this._glassContext.getCoreSvc('.Logger');
		this._ajaxService = this._glassContext.getCoreSvc('.Ajax');
	}

	/**
	 * Gets a palette with the given id from content service
	 * @param {string} id - the ID of the palette to retreive
	 * @return {Promise} Resolved with the palette or Rejected if the palette wasn't found
	 */
	getPalette(id) {
		const safeId = this.getCMSafePaletteId(id);

		if (this._cachedPalettes[safeId]) {
			return Promise.resolve(_.extend({}, this._cachedPalettes[safeId]));
		} else if (this._paletteRequestPromise[safeId]) {
			// If we've already queried for the palette and we're waiting for the response return the initial Promise.  This
			// Can happen on dashboard open when multiple widgets as for the same palette definition at the same time
			return this._paletteRequestPromise[safeId];
		}

		this._paletteRequestPromise[safeId] = this._ajaxService.ajax({
			url: `v1/palettes/${encodeURIComponent(safeId)}`,
			type: 'GET',
			headers: {
				'Accept': 'application/json'
			}
		}).then((response = {}) => {
			delete this._paletteRequestPromise[safeId];
			this._cachedPalettes[safeId] = response.data && response.data.data && response.data.data[0];
			return _.extend({}, this._cachedPalettes[safeId]);
		}, (e) => {
			delete this._paletteRequestPromise[safeId];
			this._logger.error(e);
			throw e;
		});

		return this._paletteRequestPromise[safeId];
	}

	/**
	 * Returns all the palettes found in the content store
	 * @param {String} [fillType] - Specify either 'simple' or 'continuous' palettes to filter the list of palette being returned.  Default will return all the palettes
	 * @return {object} 
	 */
	getPalettes(fillType) {
		if (this._cachedMyPalettes && this._cachedPublicPalettes) {
			return Promise.resolve(this._getCachedPalettes(fillType));
		}

		return Promise.all([this.getMyPalettes(), this.getPublicPalettes()]).then(() => {
			return this._getCachedPalettes(fillType);
		});
	}

	/**
	 * Get the cached palettes of a certain type
	 * @param {String} fillType - The type of palette ('simple', 'continuous') to return
	 * @return {Object} - Returns an object with a 'my' and 'public' array property that will contain the cached palettes
	 */
	_getCachedPalettes(fillType) {
		const myPalettes = [];
		const publicPalettes = [];

		Object.keys(this._cachedPalettes).forEach((key) => {
			const palette = this._cachedPalettes[key];
			const type = palette.content && palette.content.fillType;
			if (!fillType || type === fillType) {
				if (palette.my) {
					myPalettes.push(_.extend({}, palette));
				}
				if (palette.public) {
					publicPalettes.push(_.extend({}, palette));
				}
			}
		});

		return {
			my: myPalettes,
			public: publicPalettes
		};
	}

	/**
	 * Given an array of palettes, make sure all the palettes are cached and save their IDs in the array padded in
	 * @param {Array} palettes - Array pf palettes returned by CM
	 * @param {String} property - Property 'my' or 'public' to use to know which list the palette belongs to
	 */
	_cachePalettes(palettes, property) {
		palettes.forEach((palette) => {
			if (!this._cachedPalettes[palette.id]) {
				this._cachedPalettes[palette.id] = palette;
			}
			this._cachedPalettes[palette.id][property] = true;
		});
	}

	/**
	 * Returns all the palettes owned by the current user
	 */
	getMyPalettes() {
		if (this._cachedMyPalettes) {
			return Promise.resolve(this._getCachedPalettes().my);
		}
		return this._getPalettes('v1/palettes/my').then((results) => {
			this._cachePalettes(results, 'my');
			this._cachedMyPalettes = true;
			return results;
		});
	}

	/**
	 * Returns all the public palettes stored in content service
	 */
	getPublicPalettes() {
		if (this._cachedPublicPalettes) {
			return Promise.resolve(this._getCachedPalettes().public);
		}
		return this._getPalettes('v1/palettes/public').then((results) => {
			this._cachePalettes(results, 'public');
			this._cachedPublicPalettes = true;
			return results;
		});
	}

	/**
	 * Deletes a palette from content store
	 * @param {string} id - the id of the palette to delete
	 * @return {Promise}
	 */
	deletePalette(id) {
		const safeId = this.getCMSafePaletteId(id);
		if (!safeId) {
			Promise.reject('Need the ID of the palette to update');
		}

		return this._getPaletteName(safeId).then((name) => {
			return this._showConfirmationDlg(StringResources.get('deleteDialogTitle'), StringResources.get('deleteMessage', { name: name })).then(() => {
				return this._ajaxService.ajax({
					url: `v1/palettes/${encodeURIComponent(safeId)}?forced=true`,
					type: 'DELETE',
					headers: {
						'Content-Type': 'application/json'
					}
				}).then((response) => {
					delete this._cachedPalettes[safeId];
					this._showToast(StringResources.get('deleteToast', { name: name }));
					this.trigger('palette:deleted', {
						paletteId: safeId
					});
					return response;
				}, (e) => {
					// Unknown error happened
					this._showUnknownError(e);
					throw e;
				});
			}, () => {
				// If showConfirmationDlg rejected the promise then the user clicked on cancel
				return;
			});
		});
	}

	_getPaletteName(id) {
		return this.getPalette(id).then((palette) => {
			return palette.defaultName;
		});
	}

	/**
	 * Create a palette in the content store
	 * @param {Object} Object.palette - the palette definition to save in CM
	 * @param {Boolean} Object.public - whether the palette should be made public or not
	 */
	createPalette(options) {
		const palette = options.palette;
		const name = palette.label;
		if (!name) {
			return Promise.reject('No name specified in the palette definition');
		}

		const paletteObj = {
			defaultName: name,
			content: palette
		};

		if (typeof options.public !== 'undefined') {
			paletteObj.public = options.public;
		}

		return this._ajaxService.ajax({
			url: 'v1/palettes/my',
			type: 'POST',
			headers: {
				'Content-Type' : 'application/json'
			},
			data: JSON.stringify(paletteObj)
		}).then((response = {}) => {
			let location = response.jqXHR && response.jqXHR.getResponseHeader('location');
			if (location) {
				const paletteId = location ? location.substring(location.lastIndexOf('/') + 1) : null;
				if (paletteId) {
					// Make sure what we cache matches what a CM response would look like
					paletteObj.id = paletteId;
					paletteObj.my = true;  // We just created it, so it should go in the 'my' list of palettes.
					this._cachedPalettes[paletteId] = paletteObj;
				}
				this._showToast(StringResources.get('createdToastMessage', { name: name }));
				this.trigger('palette:created', {
					paletteId: paletteId
				});
				return paletteId;
			}
		}, (e) => {
			// Unknown error happened
			this._showUnknownError(e);
			throw e;
		});
	}


	/**
	 * Updates a palette definition in CM
	 * @param {Object} Object.id - The ID of the palette to update
	 * @param {Object} [Object.palette] - The new definition
	 * @param {Boolean} [Object.public] - Whether the palette is public or not
	 */
	updatePalette(options) {
		const { id, palette } = options;
		const safeId = this.getCMSafePaletteId(id);
		const isPublic = typeof options.public === 'undefined' ? null : options.public;
		if (!safeId) {
			Promise.reject('Need the ID of the palette to update');
		}
		const data = {};
		this._applyUpdateToPalette(data, palette, isPublic);

		return this._ajaxService.ajax({
			url: `v1/palettes/${encodeURIComponent(safeId)}`,
			type: 'PUT',
			headers: {
				'Content-Type': 'application/json'
			},
			data: JSON.stringify(data)
		}).then((response) => {
			const cachedPalette = this._cachedPalettes[safeId];
			if (cachedPalette) {
				this._applyUpdateToPalette(cachedPalette, palette, isPublic);
			}
			this.trigger('palette:updated', {
				paletteId: safeId
			});
			return response;
		}, (e) => {
			// Unknown error happened
			this._showUnknownError(e);
			throw e;
		});
	}

	_applyUpdateToPalette(palette, paletteDefinition, isPublic) {
		if (paletteDefinition) {
			palette.defaultName = paletteDefinition.label;
			palette.content = paletteDefinition;
		}
		if (isPublic != null) {
			palette.public = isPublic;
		}
	}

	_showUnknownError(e) {
		this._glassContext.appController.showErrorMessage(StringResources.get('errorCreatingPalette'), StringResources.get('paletteErrorTitle'));
		this._logger.error(e);
	}

	_showToast(msg) {
		this._glassContext.appController.showToast(msg);
	}

	_showConfirmationDlg(title, message) {
		return new Promise((resolve, reject) => {
			this._glassContext.appController.showMessage(message, title, 'info', ['ok','cancel'], undefined, function(evt) {
				if (evt.btn === 'ok') {
					resolve();
				} else {
					reject();
				}
			});
		});
	}

	_getPalettes(url) {
		return this._ajaxService.ajax({
			url: url,
			type: 'GET',
			headers: {
				'Accept': 'application/json'
			}
		}).then((response = {}) => {
			return response.data && response.data.data;
		}, (e) => {
			this._logger.error(e);
			return [];
		});
	}

	/**
	 * Some of our internal ID have prefixes.  Make sure we're dealing with the real CM ID
	 * @param {String} id 
	 */
	getCMSafePaletteId(id) {
		if (id && id.indexOf('__CM__') === 0) {
			return id.split('__CM__')[1];
		}

		return id;
	}

	/**
	 * Gets the list of favorite colors
	 */
	getFavoriteColors() {
		if (this._cachedFavoriteColors) {
			return Promise.resolve(_.clone(this._cachedFavoriteColors));
		}

		return this._ajaxService.ajax({
			url: 'v1/users/~/favColors',
			type: 'GET',
			headers: {
				'Accept': 'application/json'
			}
		}).then((response) => {
			if (!response || !response.data || !response.data[0] || !response.data[0].colors) {
				return [];
			}

			this._cachedFavoriteColors = response.data[0].colors;
			return _.clone(this._cachedFavoriteColors);
		}, (e) => {
			this._logger.error(e);
			return [];
		});
	}

	/**
	 * Saves the list of favorite colors
	 * @param {Array} colors 
	 */
	setFavoriteColors(colors) {
		if (!colors) {
			return Promise.resolve();
		}

		this._cachedFavoriteColors = colors;

		return this._ajaxService.ajax({
			url: 'v1/users/~/favColors/data',
			type: 'PUT',
			headers: {
				'Content-Type' : 'application/json'
			},
			data: JSON.stringify({ colors: this._cachedFavoriteColors } )
		}).then(() => {
			return;
		}, (e) => {
			this._logger.error(e);
			return;
		});
	}

	/**
	 * Gets the array of recently used palette info.
	 * @param {String} fillType - The fillType of the MRU list (basic or continuous)
	 */
	getRecentlyUsedPaletteInfo(fillType) {
		if (this._cachedMRUPalettes) {
			if (!this._cachedMRUPalettes[fillType]) {
				this._cachedMRUPalettes[fillType] = [];
			}
			return Promise.resolve(this._cachedMRUPalettes[fillType]);
		}

		return this._ajaxService.ajax({
			url: 'v1/users/~/mruPalettes',
			type: 'GET',
			headers: {
				'Accept': 'application/json'
			}
		}).then((response) => {
			if (!response || !response.data || !response.data[0]) {
				this._cachedMRUPalettes = {};
			} else {
				this._cachedMRUPalettes = response.data[0];
			}

			if (!this._cachedMRUPalettes[fillType]) {
				this._cachedMRUPalettes[fillType] = [];
			}

			return this._cachedMRUPalettes[fillType];
		}, (e) => {
			this._logger.error(e);
			return [];
		});
	}

	/**
	 * Adds palette information to the MRU list
	 * @param {String} Object.palette - The palette that's being added to the MRU list
	 * @param {String} Object.component - Which component dashboard or reporting is adding the palette
	 * @param {String} Object.isSystem - is the current palette a system palette
	 * @param {String} [Object.propName] - the name of the property the palette is being used for.
	 * @param {Boolean} [Object.trimSystemByPropName] - Should we match the prop name when removing system palettes from the MRU
	 */
	addRecentlyUsedPalette({ palette, component, isSystem, propName, trimSystemByPropName }) {
		if (!palette) {
			return;
		}
		const fillType = palette.content.fillType === 'ImgPath' ? 'continuous' : palette.content.fillType;

		return this.getRecentlyUsedPaletteInfo(fillType).then((recentlyUsedPaletteInfo) => {
			if (!recentlyUsedPaletteInfo) {
				return;
			}

			const safeId = this.getCMSafePaletteId(palette.id);
			let found = false;
			// If we already have this palette in the MRU just move it to the front
			for (let i = 0; i < recentlyUsedPaletteInfo.length; i++) {
				if (recentlyUsedPaletteInfo[i].id === safeId) {
					const matchedInfo = recentlyUsedPaletteInfo.splice(i, 1);
					recentlyUsedPaletteInfo.unshift(matchedInfo[0]);
					found = true;
				}
			}

			if (!found) {
				// Add the new palette information to the array
				recentlyUsedPaletteInfo.unshift({
					id: safeId,
					component: component,
					isSystem: isSystem,
					propName: propName
				});
			}

			this._trimPaletteMRU(recentlyUsedPaletteInfo, propName, trimSystemByPropName);

			return this._ajaxService.ajax({
				url: 'v1/users/~/mruPalettes/data',
				type: 'PUT',
				headers: {
					'Content-Type' : 'application/json'
				},
				data: JSON.stringify( this._cachedMRUPalettes )
			}).then(() => {
				return;
			}, (e) => {
				this._logger.error(e);
				return;
			});
		});
	}

	/**
	 * Since dashboard and reporting system palettes don't match, match sure we leave 3 valid palettes
	 * in the MRU list for each component
	 * @param {Array} recentlyUsedPaletteInfo 
	 * @param {String} propName - the name of the property the palette is being used for.
	 * @param {Boolean} trimSystemByPropName - Should we match the prop name when removing system palettes from the MRU
	 */
	_trimPaletteMRU(recentlyUsedPaletteInfo, propName, trimSystemByPropName) {
		let dashboardMatch = 0;
		let reportingMatch = 0;

		for (let i = 0; i < recentlyUsedPaletteInfo.length; i++) {
			// If we've already matched 3 palettes for dashboard and authoring and we're
			// not matching on prop name then simply remove the palette
			if (dashboardMatch >= 3 && reportingMatch >= 3 && !trimSystemByPropName) {
				recentlyUsedPaletteInfo.pop();
				i--;
				continue;
			}

			const paletteInfo = recentlyUsedPaletteInfo[i];

			if (!paletteInfo.isSystem || !trimSystemByPropName || propName === paletteInfo.propName) {
				if (paletteInfo.component === 'dashboard') {
					// Only remove it if it's a system palette and we already have our 3 matches.  Otherwise
					// it's a custom palette and will be needed by authoring for their 3 matches.
					if (paletteInfo.isSystem && dashboardMatch >= 3) {
						recentlyUsedPaletteInfo.splice(i, 1);
						i--;
					} else {
						dashboardMatch++;
					}

					// Any non system palette is valid for both reporting and dashboards
					if (!paletteInfo.isSystem) {
						reportingMatch++;
					}
				} else {
					// Only remove it if it's a system palette and we already have our 3 matches.  Otherwise
					// it's a custom palette and will be needed by dashboards for their 3 matches.
					if (paletteInfo.isSystem && reportingMatch >= 3) {
						recentlyUsedPaletteInfo.splice(i, 1);
						i--;
					} else {
						reportingMatch++;
					}

					// Any non system palette is valid for both reporting and dashboards
					if (!paletteInfo.isSystem) {
						dashboardMatch++;
					}
				}
			}
		}

		// Last safe guard, we really should never get over 9 palettes in the MRU.  Worst
		// case would be all system palettes, 3 heat, 3 confitional and 3 authoring.  Give it a little
		// more room at 12, but remove anything past that so the MRU list won't grow out of control in the
		// case of issues.
		while (recentlyUsedPaletteInfo.length > 12) {
			recentlyUsedPaletteInfo.pop();
		}
	}
}

export default PaletteService;


// WEBPACK FOOTER //
// ./src/js/services/PaletteService.js